import React, { useState, useEffect, useContext } from "react";
import { DataContext } from "../context/DataContext";
import { trackEvent } from "../utils/telemetry";
import {
  Text,
  Stack,
  Paper,
  Divider,
  useMantineTheme,
  Loader,
  Button,
  Group,
  Collapse,
  UnstyledButton,
} from "@mantine/core";
import {
  IconChevronDown,
  IconChevronUp,
  IconDeviceMobile,
} from "@tabler/icons-react";
import QRCode from "react-qr-code";
import useStatusCheck from "../hooks/useStatusCheck";
import useEdgeStatusCheck from "../hooks/useEdgeStatusCheck";
import useLocale from "../hooks/useLocale";

export default function CompactHandoff({ nextPath, urlOverride, onQrScanned }) {
  const { settings, text, data, dispatch, postPayloadMessage } = useContext(DataContext);
  const locale = useLocale("handoff");
  const theme = useMantineTheme();
  const [url, setUrl] = useState("");
  const [qrFound, setQrFound] = useState(false);
  const [isTimeUp, setIsTimeUp] = useState(false);
  const [hasNotifiedHandoff, setHasNotifiedHandoff] = useState(false);
  // QR is collapsed by default so it doesn't compete with the
  // primary "Allow Camera Access" CTA above. Tapping the
  // disclosure expands the code; polling for QR scan runs the
  // whole time, so the moment the user reveals + scans it on
  // their phone the parent already knows.
  const [showQr, setShowQr] = useState(false);

  // Use edge status check when edge is true
  const isEdgeMode = data?.edge === true;

  // Custom handler for edge mode to detect HANDED_OFF state
  const handleEdgeStatus = (result) => {
    const { process_state: processState, status } = result || {};

    // If completed, dispatch status and stop polling
    if (processState === "COMPLETED") {
      if (status === "PASS") {
        dispatch({ type: "SET_STATUS", payload: "PASS" });
        dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
        return false; // Stop polling
      }
      dispatch({ type: "SET_STATUS", payload: "FAIL" });
      dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
      return false; // Stop polling
    }

    // If status is PASS or FAIL without COMPLETED state
    if (status === "PASS") {
      dispatch({ type: "SET_STATUS", payload: "PASS" });
      dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
      return false; // Stop polling
    }

    if (status === "FAIL") {
      dispatch({ type: "SET_STATUS", payload: "FAIL" });
      dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
      return false; // Stop polling
    }

    // If handed off, show progress screen
    if (processState === "HANDED_OFF" && !qrFound) {
      setQrFound(true);
      if (onQrScanned) {
        onQrScanned(true);
      }

      // Send postMessage that user has moved to mobile device
      if (postPayloadMessage && !hasNotifiedHandoff) {
        setHasNotifiedHandoff(true);
        postPayloadMessage({
          event: "USER_MOVED_TO_MOBILE",
          app: nextPath || "handoff",
        });
      }
    }

    // Continue polling
    return true;
  };

  if (isEdgeMode) {
    useEdgeStatusCheck(true, handleEdgeStatus, true); // Start polling immediately in edge mode with custom handler and handoff flag
  } else {
    useStatusCheck(qrFound, true, "handoff");
  }

  // Handoff funnel on the original device: QR displayed → QR scanned.
  // `once` also dedupes against the full-page Handoff when both render
  // in one session.
  useEffect(() => {
    trackEvent("handoff_shown", { path: nextPath || "handoff" }, { once: true });
  }, []);
  useEffect(() => {
    if (qrFound) {
      trackEvent("handoff_scanned", { path: nextPath || "handoff" }, { once: true });
    }
  }, [qrFound]);

  // Notify parent when QR is scanned
  useEffect(() => {
    if (qrFound) {
      if (onQrScanned) {
        onQrScanned(true);
      }

      // Send postMessage that user has moved to mobile device (for non-edge mode)
      if (postPayloadMessage && !hasNotifiedHandoff) {
        setHasNotifiedHandoff(true);
        postPayloadMessage({
          event: "USER_MOVED_TO_MOBILE",
          app: nextPath || "handoff",
        });
      }
    }
  }, [qrFound, onQrScanned, postPayloadMessage, nextPath, hasNotifiedHandoff]);

  useEffect(() => {
    // Get all current URL query parameters
    const urlParams = new URLSearchParams(window.location.search);
    const frontendParam = urlParams.get('frontend');
    const edgeParam = urlParams.get('edge');

    // Preserve all existing query parameters
    urlParams.set('qr', 'true');
    // Mobile handoff receiver has already seen the verification
    // notice on desktop — carry a flag so App.js can bypass it
    // and drop the user straight into the journey.
    urlParams.set('notice_shown', 'true');
    if (nextPath) {
      urlParams.set('nextPath', nextPath);
    }
    // Preserve edge parameter if it exists
    if (edgeParam) {
      urlParams.set('edge', edgeParam);
    }
    // Pass selected test case to handoff device
    if (data?.selected_test_case_id) {
      urlParams.set('test_case_id', data.selected_test_case_id);
    }
    const queryString = urlParams.toString();

    const hostname = window.location.hostname;
    const isLocalhost =
      hostname === "localhost" ||
      hostname.startsWith("127.0.") ||
      hostname.startsWith("0.0.0.0");

    if (isLocalhost) {
      if (urlOverride) {
        setUrl(urlOverride);
      } else {
        setUrl(
          `https://43e1b5838093.ngrok.app/${settings.request_id}?${queryString}`
        );
      }
    } else if (hostname.endsWith("fe-dcams.pages.dev")) {
      // For Cloudflare Pages preview domains
      setUrl(`${window.location.origin}/${settings.request_id}?${queryString}`);
    } else {
      // Determine frontend to use based on hostname or query param
      const useVxFrontend = hostname === "vx-fe.dcams.app" || frontendParam === "vx-fe";

      let urlToUse;
      if (useVxFrontend) {
        urlToUse = `https://vx-fe.dcams.app/${settings.request_id}?${queryString}`;
      } else if (hostname.endsWith("dcams.app")) {
        urlToUse = `${window.location.origin}/${settings.request_id}?${queryString}`;
      } else {
        urlToUse = `${process.env.REACT_APP_FRONTEND_URL}/${settings.request_id}?${queryString}`;
      }
      setUrl(urlToUse);
    }
  }, [settings, nextPath, urlOverride]);

  // Poll for QR scan
  useEffect(() => {
    // Skip QR polling for edge mode - edge status hook handles everything
    if (isEdgeMode) {
      return;
    }

    const fetchQrStatus = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/v3/stable/api/frontend/qrStatus`,
          {
            method: "POST",
            body: JSON.stringify({
              request_id: settings.request_id,
              type: "get",
              site_name: settings.site_name,
            }),
          }
        );
        const data = await response.json();

        if (data.found) {
          setQrFound(true); // Stop fetching once QR is found
        }
      } catch (error) {
        console.error("Error fetching QR status:", error);
      }
    };

    if (!qrFound) {
      const intervalId = setInterval(fetchQrStatus, 6000);
      return () => clearInterval(intervalId);
    }
  }, [qrFound, settings, isEdgeMode]);

  // 5 minute timeout
  useEffect(() => {
    const timerId = setTimeout(
      () => {
        setIsTimeUp(true);
      },
      5 * 60 * 1000
    ); // 5 minutes

    return () => clearTimeout(timerId);
  }, []);

  const restartFetch = () => {
    setIsTimeUp(false);
    setQrFound(false);
  };

  // Show progress screen when QR is scanned
  if (qrFound) {
    return (
      <>
        <Divider my="md" />
        <Stack align="center" spacing="sm">
          <Loader size="sm" />
          <Stack spacing={0} align="center">
            <Text fz="sm" fw={600} align="center">
              {text.handoff?.inProgress?.title || "Do not close the browser"}
            </Text>
            <Text c="dimmed" fz="xs" align="center">
              {text.handoff?.inProgress?.subTitle || "Please complete the process on your other device and return"}
            </Text>
          </Stack>
          {isTimeUp && (
            <Button variant="outline" size="xs" onClick={restartFetch}>
              {text.handoff?.inProgress?.button || "Check my status"}
            </Button>
          )}
        </Stack>
      </>
    );
  }

  const primary = theme.colors[theme.primaryColor]?.[6] || theme.colors.blue[6];

  return (
    <>
      <Divider my="xs" />
      <Stack align="center" spacing={6}>
        <UnstyledButton
          onClick={() => setShowQr((v) => !v)}
          aria-expanded={showQr}
          style={{
            color: primary,
            fontWeight: 600,
            fontSize: 12,
            lineHeight: 1,
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            padding: "2px 6px",
            borderRadius: 6,
            border: "none",
            outline: "none",
          }}
        >
          <IconDeviceMobile size={13} stroke={2} />
          <span style={{ textDecoration: "underline" }}>
            {text.handoff?.compactTitle ||
              locale?.compactTitle ||
              "Or continue on your phone"}
          </span>
          {showQr ? (
            <IconChevronUp size={13} stroke={2} />
          ) : (
            <IconChevronDown size={13} stroke={2} />
          )}
        </UnstyledButton>
        <Collapse in={showQr}>
          <Stack align="center" spacing={4} style={{ paddingTop: 2 }}>
            <QRCode size={88} value={url} viewBox={`0 0 256 256`} />
            <Text c="dimmed" fz={10} align="center">
              {text.handoff?.compactExplanation ||
                locale?.compactExplanation ||
                "Scan to continue on mobile"}
            </Text>
          </Stack>
        </Collapse>
      </Stack>
    </>
  );
}
